import type { CSSProperties } from 'react';
import type { HeroConfig } from './types';

// Escapes a background-image URL for safe use inside a CSS url("...") value.
export function toCssUrl(value?: string): string {
  if (!value) return 'none';

  const escapedValue = value
    .replace(/\\/g, '\\\\')
    .replace(/"/g, '\\"')
    .replace(/[\r\n\f]/g, (character) => `\\${character.charCodeAt(0).toString(16)} `);

  return `url("${escapedValue}")`;
}

export interface HeroStackStyleOptions {
  /** Color secundario → Reservá Online / Buscar (Institucional / Ecommerce). */
  reserveButtonColor?: string;
  /** Color de los iconos de buscadores → fondo de tabs del panel Institucional. */
  iconColor?: string;
}

// CSS custom properties for the Header+Hero wrapper (`.preview-hero-stack`). `isFullBleed`
// is driven by headerConfig.fullBleed (not heroConfig.fullBleed — a separate flag) since
// that's what decides whether the composition gets hoisted onto this shared ancestor, so
// the header's mobile searcher-nav (a sibling of .hero, not a descendant) can also inherit
// the brand color. Shared between PreviewRoute and the real storefront route (UnifiedLayout)
// so both stay visually identical.
//
// `--reserve-button-color` / `--icon-color` live on the stack (not only the <header>) so the
// reserve search panel — a sibling of the topbar — and any hero search forms inherit them.
export function getHeroStackStyle(
  heroConfig: Partial<HeroConfig>,
  isFullBleed: boolean,
  options: HeroStackStyleOptions = {},
): CSSProperties {
  const desktopBackground = toCssUrl(heroConfig.backgroundImageUrl);
  const reserveButtonColor = options.reserveButtonColor || heroConfig.brandColor2 || '#152956';
  const iconColor = options.iconColor || '#225e8f';

  return {
    '--brand': heroConfig.brandColor || '#54a5e8',
    '--hero-brand': heroConfig.brandColor || '#54a5e8',
    '--hero-brand-2': heroConfig.brandColor2 || '#19a899',
    '--reserve-button-color': reserveButtonColor,
    '--icon-color': iconColor,
    ...(isFullBleed && {
      '--hero-bg-image': desktopBackground,
      '--hero-mobile-bg-image': heroConfig.mobileBackgroundImageUrl
        ? toCssUrl(heroConfig.mobileBackgroundImageUrl)
        : desktopBackground,
      '--hero-overlay-opacity': (heroConfig.overlay ?? 42) / 100,
    }),
  } as CSSProperties;
}
